import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, KeyRound, CheckCircle2, AlertCircle, Mail } from 'lucide-react';

// Map backend `detail.code` → human copy + recovery UX. The keys MUST match
// /app/backend/routers/auth.py POST /reset-password contract.
const ERROR_COPY = {
  LINK_INVALID_OR_USED: {
    title: 'Reset link no longer valid',
    body:
      "This link has already been used, or a newer reset email replaced it. " +
      "If you requested multiple reset emails, only the most recent link works.",
    cta: 'Send a fresh link',
  },
  LINK_EXPIRED: {
    title: 'Reset link expired',
    body: 'Reset links last 30 minutes. Request a fresh one and try again.',
    cta: 'Send a fresh link',
  },
  INVALID_REQUEST: {
    title: 'Could not reset',
    body: 'Please check your password (at least 6 characters) and try again.',
    cta: null,
  },
};

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token] = useState(searchParams.get('token') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // null = no error; { code, title, body, cta } when set
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);
  // V14.5 Part 19l — true while we resolve token→email for WhatsApp
  // URL-button links that carry only `?token=…` (no email param).
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link.');
      navigate('/login');
      return;
    }
    if (email) return; // email already in URL — nothing to resolve

    // Token-only link (WhatsApp URL-button) — fetch the email so we can
    // show "Resetting password for X" and surface expired/invalid states
    // BEFORE the user types a new password.
    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        const { data } = await api.get('/api/auth/reset-info', { params: { token } });
        if (cancelled) return;
        setEmail(data?.email || '');
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data || {};
        const code = detail.detailObject?.code;
        if (code && ERROR_COPY[code]) {
          setError({ ...ERROR_COPY[code], code });
        } else {
          setError({
            title: 'Reset link no longer valid',
            body: 'Please request a fresh link from the Sign-in page.',
            cta: null,
          });
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, email, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 6) return toast.error('Password must be at least 6 characters.');
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      // V14.5 Part 19l — backend now accepts token-only requests; email
      // is omitted for WhatsApp URL-button links that couldn't resolve.
      const payload = { token, new_password: pw };
      if (email) payload.email = email;
      await api.post('/api/auth/reset-password', payload);
      setDone(true);
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      // V14.5 Part 17k — `detail` is now always a string (api interceptor
      // flattens FastAPI's `{code, message}` envelope). Structured payload
      // is preserved on `detailObject` so we can still map `code` → ERROR_COPY.
      const data = err?.response?.data || {};
      const code = data.detailObject?.code;
      const message = data.detailObject?.message || data.detail;
      if (code) {
        setError({ ...ERROR_COPY[code], code, message });
      } else if (typeof data.detail === 'string' && data.detail) {
        setError({ title: 'Could not reset', body: data.detail, cta: null });
      } else {
        setError({ title: 'Could not reset', body: 'Something went wrong. Try again.', cta: null });
      }
    }
    setLoading(false);
  };

  const requestFreshLink = async () => {
    // V14.5 Part 19l — token-only links can land here without an email
    // in scope (WhatsApp URL-button + expired token). Send them back to
    // the Sign-in page where they can re-enter the email.
    if (!email) {
      navigate('/login?mode=forgot');
      return;
    }
    setResending(true);
    try {
      await api.post('/api/auth/forgot-password', { email });
      toast.success(`Fresh reset link sent to ${email}. Check your inbox.`);
      setTimeout(() => navigate('/login'), 1800);
    } catch {
      toast.error('Could not send a fresh link. Try again from the Sign-in page.');
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 h-20 w-20 rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-sm border">
            <img src="/logo.png" alt="Soul Sanskar" className="h-28 w-28 object-contain scale-150" />
          </div>
          <h1 className="font-heading text-2xl font-semibold">Reset password</h1>
        </div>

        <Card className="rounded-2xl border bg-white shadow-sm">
          <CardContent className="pt-6 pb-6">
            {done ? (
              <div className="text-center py-2" data-testid="reset-password-success">
                <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
                <h2 className="font-heading text-lg font-semibold mb-1">Password updated</h2>
                <p className="text-sm text-muted-foreground">Redirecting to sign-in…</p>
              </div>
            ) : resolving ? (
              <div className="text-center py-6" data-testid="reset-password-resolving">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Verifying your reset link…</p>
              </div>
            ) : error ? (
              <div className="text-center py-2 space-y-4" data-testid="reset-password-error">
                <AlertCircle className="h-12 w-12 text-rose-600 mx-auto" />
                <div>
                  <h2 className="font-heading text-lg font-semibold mb-1">{error.title}</h2>
                  <p className="text-sm text-muted-foreground">{error.body}</p>
                </div>
                {error.cta ? (
                  <Button
                    onClick={requestFreshLink}
                    disabled={resending}
                    className="w-full"
                    data-testid="resend-reset-link-button"
                  >
                    {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <><Mail className="h-4 w-4 mr-2" /> {error.cta}</>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setError(null)}
                    className="w-full"
                    data-testid="reset-try-again-button"
                  >
                    Try again
                  </Button>
                )}
                <Button variant="ghost" className="w-full" onClick={() => navigate('/login')}
                        data-testid="back-to-signin-button">
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
                <p className="text-sm text-muted-foreground mb-2">
                  {email ? <>Resetting password for <strong>{email}</strong></> : 'Set a new password for your account'}
                </p>
                <div>
                  <Label htmlFor="pw" className="text-xs uppercase tracking-wide text-muted-foreground">New password</Label>
                  <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 6 characters"
                    data-testid="reset-pw-input" required minLength={6} />
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-xs uppercase tracking-wide text-muted-foreground">Confirm password</Label>
                  <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                    data-testid="reset-pw2-input" required />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-2" /> Update password</>}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
